import { desanitizeKey } from '@/helper/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React, { useEffect, useRef } from 'react';
import CustomScrollbar from './CustomScrollbar';

interface DropdownWithRadioProps {
  label: string;
  options: string[];
  selected: string | null | undefined;
  onChange: (value: string | null) => void;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

const DropdownWithRadio: React.FC<DropdownWithRadioProps> = ({
  label,
  options,
  selected,
  onChange,
  isOpen,
  onOpen,
  onClose,
  placeholder,
  disabled = false,
  className,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState('left-0');
  const [dropdownVertical, setDropdownVertical] = React.useState('top-full mt-2');
  const [searchTerm, setSearchTerm] = React.useState('');

  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleSelect = (option: string) => {
    const isAllOption = option.startsWith('All ');
    onChange(isAllOption ? null : option);
    onClose();
    setSearchTerm('');
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
        setSearchTerm('');
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        onClose();
        setSearchTerm('');
      } else if (event.key === 'Backspace') {
        setSearchTerm((prev) => prev.slice(0, -1));
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
        setSearchTerm((prev) => prev + event.key);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);

      // Check if dropdown goes off screen and adjust position
      if (dropdownRef.current) {
        const rect = dropdownRef.current.getBoundingClientRect();
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        const availableRightSpace = screenWidth - rect.left - 20;
        const availableBottomSpace = screenHeight - rect.bottom - 20;
        const dropdownHeight = Math.min(240, options.length * 40); // Estimate dropdown height

        // Horizontal positioning
        if (availableRightSpace < rect.width * 1.5) {
          setDropdownPosition('right-0');
        } else {
          setDropdownPosition('left-0');
        }

        // Vertical positioning
        if (availableBottomSpace < dropdownHeight) {
          setDropdownVertical('bottom-full mb-2');
        } else {
          setDropdownVertical('top-full mt-2');
        }
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div
      ref={dropdownRef}
      // className={`flex flex-row relative  border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm ${className ?? ""}`}
      className={
        className?.trim()
          ? className
          : 'flex flex-row relative border border-gray-300 rounded-lg bg-[#F2E9EA] text-sm'
      }
    >
      <button
        type="button"
        onClick={() => (isOpen ? onClose() : onOpen())}
        className="text-[#4A082D] h-full rounded-xl px-3 py-[6px] w-full flex justify-between items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={disabled}
      >
        <span className="truncate text-left">
          {isOpen && searchTerm ? searchTerm : selected || placeholder || 'Select All'}
        </span>

        {isOpen ? (
          <ChevronUp size={16} className="flex-shrink-0 text-[#4A082D]" />
        ) : (
          <ChevronDown size={16} className="flex-shrink-0 text-[#4A082D]" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute ${dropdownPosition} ${dropdownVertical} min-w-full w-max
    bg-white rounded-md shadow-lg
    border border-gray-200 z-[9999] py-2`}
        >
          <CustomScrollbar>
            {filteredOptions.map((option) => (
              <label
                key={option}
                className="flex items-center px-3 py-2 cursor-pointer
                       hover:bg-[#F8EAF1]  text-sm"
                style={{ width: dropdownRef.current?.offsetWidth }}
              >
                <div
                  className="w-4 h-4 rounded-full border flex
                            items-center justify-center mr-2 flex-shrink-0"
                >
                  <div
                    className={`w-3 h-3 rounded-full ${
                      selected === option || (!selected && option.startsWith('All '))
                        ? 'bg-[#621C43]'
                        : 'bg-transparent'
                    }`}
                  />
                </div>

                <input
                  type="radio"
                  name={label}
                  value={option}
                  checked={selected === option || (!selected && option.startsWith('All '))}
                  onChange={() => handleSelect(option)}
                  className="hidden"
                />

                <span className="break-words">{desanitizeKey(option)}</span>
              </label>
            ))}
            {filteredOptions.length === 0 && (
              <div className="px-3 py-2 text-gray-500 text-center text-sm">No results found</div>
            )}
          </CustomScrollbar>
          {/* </div> */}
        </div>
      )}
    </div>
  );
};

export default DropdownWithRadio;
